// kanjo/Order.jsx — Order guide, wired to live procurement data (US-2.10–2.12).

function ParBar({ have, par, max }) {
  const w = 56;
  const safeMax = Math.max(1, max);
  const pct = Math.min(1, have / safeMax);
  const parX = (par / safeMax) * w;
  const low = have < par;
  return (
    <div style={{ position: 'relative', width: w, height: 6, borderRadius: 3, background: 'var(--surface-3)' }}>
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0,
        width: pct * 100 + '%',
        background: low ? 'var(--warning)' : 'var(--positive)',
        borderRadius: 3,
      }}/>
      <div style={{
        position: 'absolute', left: parX, top: -2, bottom: -2,
        width: 1, background: 'var(--ink)',
      }}/>
    </div>
  );
}

function useOrderGuideData() {
  const [state, setState] = React.useState({ loading: true, rows: [], cart: {}, locale: 'en-US', currency: 'USD' });
  const reload = React.useCallback(async () => {
    if (!window.kanjo) return;
    try {
      const claims = await window.kanjo.auth.currentClaims().catch(() => null);
      if (!claims) { setState((s) => ({ ...s, loading: false })); return; }
      const profile = await window.kanjo.db.userProfile.get(claims.sub);
      const rows = await window.kanjo.procurement.orderGuide.listGuide();
      // Pre-fill cart with suggested quantities for below-par items.
      const cart = {};
      for (const r of rows) if (r.status === 'below') cart[r.itemId] = r.suggestedQuantity;
      setState({ loading: false, rows, cart, locale: profile?.locale || navigator.language || 'en-US', currency: profile?.currency || 'USD' });
    } catch (e) { console.warn('[OrderGuide] load failed', e); setState((s) => ({ ...s, loading: false })); }
  }, []);
  React.useEffect(() => { reload(); }, [reload]);
  return [state, setState, reload];
}

function groupByVendor(rows) {
  const groups = new Map();
  for (const r of rows) {
    const key = r.preferredVendor ? r.preferredVendor.vendorId : '__none__';
    const name = r.preferredVendor ? vendorName(r.preferredVendor.name) : 'No vendor set';
    if (!groups.has(key)) groups.set(key, { key, vendorName: name, rows: [] });
    groups.get(key).rows.push(r);
  }
  // Vendors sorted by row count desc; orphans last.
  return [...groups.values()].sort((a, b) => {
    if (a.key === '__none__') return 1;
    if (b.key === '__none__') return -1;
    return b.rows.length - a.rows.length;
  });
}

function fmtMoney(cents, currency, locale) {
  const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format((c || 0) / 100);
}

function OrderGuide({ dark = false, persona = 'owner' }) {
  const nav = React.useContext(KanjoNav);
  const [state, setState, reload] = useOrderGuideData();
  const [filter, setFilter] = React.useState('below');
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState(null);

  const belowCount = state.rows.filter((r) => r.status === 'below').length;
  const inCartCount = Object.values(state.cart).filter((q) => q > 0).length;
  const visible = state.rows.filter((r) => {
    if (filter === 'below') return r.status === 'below';
    if (filter === 'cart')  return (state.cart[r.itemId] || 0) > 0;
    return true;
  });

  // Track items that contribute to the total without a known price so the
  // operator isn't surprised by a low headline figure.
  let unpricedCount = 0;
  const totalCents = state.rows.reduce((s, r) => {
    const q = state.cart[r.itemId] || 0;
    if (q > 0 && r.lastUnitPriceCents == null) unpricedCount += 1;
    return s + q * (r.lastUnitPriceCents || 0);
  }, 0);

  function bump(itemId, delta) {
    setState((prev) => {
      const next = Math.max(0, (prev.cart[itemId] || 0) + delta);
      return { ...prev, cart: { ...prev.cart, [itemId]: next } };
    });
  }
  function setQty(itemId, value) {
    setState((prev) => ({ ...prev, cart: { ...prev.cart, [itemId]: Math.max(0, value) } }));
  }

  function renderRow(r, i, last) {
    const qty = state.cart[r.itemId] || 0;
    const low = r.status === 'below';
    return (
      <div key={r.itemId} style={{
        display: 'grid', gridTemplateColumns: '1fr auto', gap: 14, padding: 16,
        borderBottom: last ? 'none' : '1px solid var(--rule)',
        alignItems: 'center',
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink)' }}>{r.itemName}</div>
            {low && <span className="pill warning" style={{ fontSize: 10.5, padding: '2px 7px' }}>Low</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <ParBar have={r.onHandQuantity} par={r.parQuantity} max={Math.max(r.parQuantity * 1.5, 1)}/>
            <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
              <span style={{ color: low ? 'var(--warning)' : 'var(--ink-2)', fontWeight: 500 }}>{r.onHandQuantity}</span>/{r.parQuantity} {r.uom}
            </div>
          </div>
          {r.lastUnitPriceCents == null && (
            <div style={{ fontSize: 11.5, color: 'var(--warning)' }}>No price on file</div>
          )}
        </div>
        <Stepper value={qty} onChange={(v) => setQty(r.itemId, v)} label={`Order quantity for ${r.itemName}`}/>
      </div>
    );
  }

  async function sendOrders() {
    if (sending) return;
    setSendError(null);

    // Group cart items by preferred vendor. Items without a preferred vendor
    // are collected separately and surfaced as an error — we won't silently
    // drop them.
    const groups = new Map();
    const orphans = [];
    for (const r of state.rows) {
      const qty = state.cart[r.itemId] || 0;
      if (qty <= 0) continue;
      if (!r.preferredVendor) { orphans.push(r); continue; }
      const key = r.preferredVendor.vendorId;
      if (!groups.has(key)) groups.set(key, { vendor: r.preferredVendor, lines: [] });
      groups.get(key).lines.push({ itemId: r.itemId, quantity: qty, uom: r.uom });
    }
    if (groups.size === 0) {
      setSendError(orphans.length > 0
        ? 'No items have a preferred vendor. Set one before sending.'
        : 'Add at least one item to cart first.');
      return;
    }

    setSending(true);
    try {
      const created = [];
      for (const { vendor, lines } of groups.values()) {
        const { po } = await window.kanjo.procurement.purchaseOrders.createDraft({
          vendorId: vendor.vendorId,
          lines,
        });
        // Approval is opt-in for now: submit without explicit approval, catching
        // APPROVAL_REQUIRED so the draft survives for the operator to revisit.
        try {
          await window.kanjo.procurement.purchaseOrders.submit(po.poId, { via: 'email' });
          created.push({ poId: po.poId, vendor: vendor.name, status: 'submitted' });
        } catch (e) {
          if (e.code === 'APPROVAL_REQUIRED') {
            created.push({ poId: po.poId, vendor: vendor.name, status: 'pending_approval' });
          } else {
            throw e;
          }
        }
      }
      if (orphans.length > 0) {
        setSendError(`Sent ${created.length} order${created.length === 1 ? '' : 's'}. ${orphans.length} item${orphans.length === 1 ? '' : 's'} skipped (no vendor).`);
      } else {
        window.kanjoToast?.({ kind: 'success', text: `Sent ${created.length} order${created.length === 1 ? '' : 's'}.` });
      }
      // Clear the cart and reload from disk so submitted POs disappear from the
      // "in cart" view.
      setState((s) => ({ ...s, cart: {} }));
      await reload();
      if (orphans.length === 0) nav.navigate('/buy');
    } catch (e) {
      console.warn('[OrderGuide] send failed', e);
      setSendError(e.message || 'Could not send orders. Try again.');
    } finally {
      setSending(false);
    }
  }

  return (
    <KShell dark={dark} tab="buy" persona={persona}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <KHeader back big title="Order guide" subtitle="Live par list" />

        <div style={{ padding: '0 20px 16px' }}>
          <div style={{ display: 'flex', gap: 4, padding: 4, background: 'var(--surface-3)', borderRadius: 12 }}>
            {[
              { id: 'below', label: 'Below par', n: belowCount },
              { id: 'all',   label: 'All',       n: state.rows.length },
              { id: 'cart',  label: 'In cart',   n: inCartCount },
            ].map((t) => (
              <button key={t.id} onClick={() => setFilter(t.id)} style={{
                flex: 1, padding: '8px 10px',
                background: filter === t.id ? 'var(--surface)' : 'transparent',
                borderRadius: 8, border: 'none',
                fontSize: 13, fontWeight: filter === t.id ? 600 : 500,
                color: filter === t.id ? 'var(--ink)' : 'var(--ink-3)',
                cursor: 'pointer',
              }}>
                {t.label} <span style={{ color: 'var(--ink-4)', marginLeft: 3 }}>{t.n}</span>
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px' }}>
          {visible.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--on-dim)', fontSize: 14 }}>
              {state.loading ? 'Loading…' : filter === 'below' ? 'Nothing below par right now.' : 'No items.'}
            </div>
          ) : (() => {
            const groups = groupByVendor(visible);
            const showHeaders = groups.length > 1;
            return groups.map(({ key, vendorName, rows }) => (
              <div key={key} style={{ marginBottom: 14 }}>
                {showHeaders && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px 8px' }}>
                    <div className="label">{vendorName}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--on-dim)' }}>{rows.length} item{rows.length === 1 ? '' : 's'}</div>
                  </div>
                )}
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  {rows.map((r, i) => renderRow(r, i, i === rows.length - 1))}
                </div>
              </div>
            ));
          })()}
        </div>

        <div style={{ padding: '14px 20px 28px', background: 'var(--surface)', borderTop: '1px solid var(--rule)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <div className="label">{inCartCount} item{inCartCount === 1 ? '' : 's'}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
                <div className="mono" style={{ fontSize: 20, fontWeight: 600 }}>{fmtMoney(totalCents, state.currency, state.locale)}</div>
                {unpricedCount > 0 && (
                  <span className="pill warning" style={{ fontSize: 11 }}>+ {unpricedCount} unpriced</span>
                )}
              </div>
            </div>
          </div>
          {sendError && (
            <ErrorBanner style={{ marginBottom: 10 }}>{sendError}</ErrorBanner>
          )}
          <button onClick={sendOrders}
                  disabled={sending || inCartCount === 0}
                  className="btn primary lg"
                  style={{ width: '100%', opacity: (sending || inCartCount === 0) ? 0.6 : 1, cursor: sending ? 'default' : 'pointer' }}>
            {sending ? 'Sending…' : 'Send orders'}
          </button>
        </div>
      </div>
    </KShell>
  );
}

// ─── Shopping list — build the next order for a supplier from purchase history ─
function ShoppingList({ dark = false, persona = 'owner' }) {
  const preVendor = useHashQuery('vendor'); // ?vendor=<id> pre-selects the supplier (from the Buy tab order icon)
  const [vendors, setVendors] = React.useState(null);   // null = loading
  const [vendorId, setVendorId] = React.useState('');
  const [contact, setContact] = React.useState({ orderSms: '', orderEmail: '' });
  const [suggestions, setSuggestions] = React.useState([]);
  const [qty, setQty] = React.useState({});             // key → quantity
  const [custom, setCustom] = React.useState([]);       // [{ key, name, lastPriceCents }]
  const [newItem, setNewItem] = React.useState('');
  const [currency, setCurrency] = React.useState('USD');
  const [locale, setLocale] = React.useState('en-US');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo) return;
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        const profile = claims ? await window.kanjo.db.userProfile.get(claims.sub).catch(() => null) : null;
        if (profile) { setCurrency(profile.currency || 'USD'); setLocale(profile.locale || 'en-US'); }
        const vs = await window.kanjo.procurement.shoppingList.vendorsWithHistory();
        if (cancelled) return;
        setVendors(vs);
        // Pre-select the vendor passed from the Buy tab if it has history; else the most-active.
        if (vs.length) setVendorId(vs.some((v) => v.vendorId === preVendor) ? preVendor : vs[0].vendorId);
      } catch (e) { console.warn('[ShoppingList] vendors load failed', e); if (!cancelled) setVendors([]); }
    })();
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (!vendorId || !window.kanjo) return;
    let cancelled = false;
    (async () => {
      try {
        const [sugg, draft, vendor] = await Promise.all([
          window.kanjo.procurement.shoppingList.suggestionsForVendor(vendorId),
          window.kanjo.procurement.shoppingList.loadDraft(vendorId),
          window.kanjo.db.vendors.get(vendorId).catch(() => null),
        ]);
        if (cancelled) return;
        setSuggestions(sugg);
        setQty(draft?.qty || {});
        setCustom(draft?.custom || []);
        setContact({ orderSms: vendor?.orderSms || '', orderEmail: vendor?.orderEmail || '' });
      } catch (e) { console.warn('[ShoppingList] suggestions load failed', e); }
    })();
    return () => { cancelled = true; };
  }, [vendorId]);

  const persist = React.useCallback((nextQty, nextCustom) => {
    if (!vendorId) return;
    window.kanjo.procurement.shoppingList.saveDraft(vendorId, { qty: nextQty, custom: nextCustom }).catch(() => {});
  }, [vendorId]);

  const rows = [...suggestions, ...custom];
  const setItemQty = (key, n) => {
    const v = Math.max(0, n);
    const next = { ...qty, [key]: v };
    if (v === 0) delete next[key];
    setQty(next); persist(next, custom);
  };
  const addCustom = () => {
    const name = newItem.trim(); if (!name) return;
    const key = 'custom:' + name.toLowerCase();
    if (!rows.find((r) => r.key === key)) {
      const nextCustom = [...custom, { key, name, lastPriceCents: 0 }];
      const nextQty = { ...qty, [key]: 1 };
      setCustom(nextCustom); setQty(nextQty); persist(nextQty, nextCustom);
    }
    setNewItem('');
  };
  const clearList = () => {
    setQty({}); setCustom([]);
    if (vendorId) window.kanjo.procurement.shoppingList.clearDraft(vendorId).catch(() => {});
  };

  const inList = rows.filter((r) => (qty[r.key] || 0) > 0);
  const estTotalCents = inList.reduce((s, r) => s + (qty[r.key] || 0) * (r.lastPriceCents || 0), 0);
  const M = (c) => fmtMoney(c, currency, locale);

  // Shareable plain-text version of the list.
  const supplierName = vendorName((vendors || []).find((v) => v.vendorId === vendorId)?.name, 'Supplier');
  const listTitle = `Shopping list — ${supplierName}`;
  const listText = inList.length
    ? `${listTitle}\n\n${inList.map((r) => `- ${qty[r.key]} x ${r.name}`).join('\n')}\n\nEst. total: ${M(estTotalCents)}`
    : '';
  // Pre-fill the recipient with the supplier's stored order contact (set on the
  // vendor detail screen). Falls back to a blank recipient if nothing's saved —
  // the user can still type one in their mail/messages app.
  const mailtoHref = `mailto:${encodeURIComponent(contact.orderEmail || '')}?subject=${encodeURIComponent(listTitle)}&body=${encodeURIComponent(listText)}`;
  const smsHref = `sms:${encodeURIComponent(contact.orderSms || '')}?&body=${encodeURIComponent(listText)}`;   // ?& form works on iOS + Android
  const isMobile = /Android|iPhone|iPad|iPod|Mobi/i.test(navigator.userAgent || '');
  const canShare = typeof navigator.share === 'function';
  const shareNative = async () => {
    try { await navigator.share({ title: listTitle, text: listText }); }
    catch (e) { /* user dismissed or unsupported */ }
  };

  const Stepper = ({ k }) => {
    const n = qty[k] || 0;
    const btn = { width: 30, height: 30, borderRadius: 8, border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 18, lineHeight: 1, cursor: 'pointer' };
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button aria-label="decrease" onClick={() => setItemQty(k, n - 1)} disabled={n === 0} style={{ ...btn, opacity: n === 0 ? 0.4 : 1 }}>−</button>
        <span className="mono" style={{ minWidth: 18, textAlign: 'center', fontSize: 14, fontWeight: 600 }}>{n}</span>
        <button aria-label="increase" onClick={() => setItemQty(k, n + 1)} style={btn}>+</button>
      </div>
    );
  };

  return (
    <KShell dark={dark} tab="buy" persona={persona}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <KHeader back big title="Shopping list" subtitle="Build the next order from what you usually buy" />
        <div style={{ padding: '0 16px 8px' }}>
          {vendors && vendors.length > 0 && (
            <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} aria-label="Supplier"
                    style={{ width: '100%', padding: '11px 12px', fontSize: 15, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)' }}>
              {vendors.map((v) => <option key={v.vendorId} value={v.vendorId}>{vendorName(v.name)} ({v.invoiceCount})</option>)}
            </select>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
          {vendors == null && <div style={{ padding: 24, textAlign: 'center', color: 'var(--on-dim)' }}>Loading…</div>}
          {vendors && vendors.length === 0 && (
            <Empty title="No purchase history yet" body="Scan a few receipts and your usual items will appear here to build a list." />
          )}
          {vendorId && rows.length === 0 && vendors && vendors.length > 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--on-dim)', fontSize: 13 }}>No items recorded for this supplier yet.</div>
          )}

          {rows.map((r) => {
            const n = qty[r.key] || 0;
            const sub = r.packSize || r.uom || '';
            return (
              <div key={r.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '11px 4px', borderBottom: '1px solid var(--hairline)', opacity: n > 0 ? 1 : 0.78 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: n > 0 ? 600 : 500, color: 'var(--on)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--on-dim)', marginTop: 1 }}>
                    {r.lastPriceCents ? `${M(r.lastPriceCents)}${sub ? ` · ${sub}` : ''}` : (sub || 'custom item')}
                    {r.timesPurchased ? ` · bought ${r.timesPurchased}×` : ''}
                  </div>
                </div>
                <Stepper k={r.key}/>
              </div>
            );
          })}

          {vendorId && (
            <div style={{ display: 'flex', gap: 8, margin: '14px 0 20px' }}>
              <input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addCustom(); }}
                     placeholder="Add another item…" aria-label="Add item"
                     style={{ flex: 1, padding: '10px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)' }}/>
              <button onClick={addCustom} disabled={!newItem.trim()} className="pill brand" style={{ border: 'none', padding: '0 16px', cursor: newItem.trim() ? 'pointer' : 'default', opacity: newItem.trim() ? 1 : 0.5 }}>Add</button>
            </div>
          )}
        </div>

        {inList.length > 0 && (
          <div style={{ padding: '12px 16px calc(env(safe-area-inset-bottom,0) + 12px)', borderTop: '1px solid var(--rule)', background: 'var(--bg)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 13, color: 'var(--on-dim)' }}>{inList.length} item{inList.length === 1 ? '' : 's'} · est.</div>
                <div className="mono" style={{ fontSize: 20, fontWeight: 700 }}>{M(estTotalCents)}</div>
              </div>
              <button onClick={clearList} style={{ padding: '8px 12px', fontSize: 13, borderRadius: 8, border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink-2)', cursor: 'pointer' }}>Clear</button>
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--on-dim)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Send cart to {supplierName}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <a href={smsHref} style={{ flex: 1, textAlign: 'center', padding: '11px', borderRadius: 10, border: 'none', background: 'var(--brand,#2563eb)', color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
                Text{contact.orderSms ? '' : '…'}
              </a>
              <a href={mailtoHref} style={{ flex: 1, textAlign: 'center', padding: '11px', borderRadius: 10, border: 'none', background: 'var(--brand,#2563eb)', color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
                Email{contact.orderEmail ? '' : '…'}
              </a>
              {canShare && (
                <button onClick={shareNative} aria-label="Share via system sheet"
                  style={{ width: 44, padding: '11px 0', borderRadius: 10, border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink-2)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>•••</button>
              )}
            </div>
            {(!contact.orderSms || !contact.orderEmail) && (
              <div style={{ fontSize: 11.5, color: 'var(--on-dim)', marginTop: 8 }}>
                {!contact.orderSms && !contact.orderEmail
                  ? 'No supplier contact saved — you can add a recipient in the app that opens, or set one in Vendor details.'
                  : !contact.orderSms ? 'No SMS number saved — add one in Vendor details.'
                  : 'No email saved — add one in Vendor details.'}
              </div>
            )}
          </div>
        )}
      </div>
    </KShell>
  );
}

Object.assign(window, { OrderGuide, ParBar, ShoppingList });
